'use client';

import {
  cloneElement,
  isValidElement,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ElementType,
  type ReactNode,
} from 'react';
import { animate, motion, useInView, useReducedMotion } from 'framer-motion';
import { PortableText, type PortableTextComponents } from '@portabletext/react';
import type { PortableTextBlock } from 'sanity';
import { stegaClean } from '@sanity/client/stega';

import { cn } from '@/lib/utils';
import Eyebrow from '../Eyebrow';
import { MeasuredHighlight, measuredRichTextDark } from '../richtext';

export const EXPO = [0.16, 1, 0.3, 1] as const;

const useIsoLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

const MintSerif = ({ children }: { children?: ReactNode }) => (
  <span className="!font-blacker font-normal italic text-[#A8E8D4]">{children}</span>
);

const MintHighlight = ({ children }: { children?: ReactNode }) => (
  <MeasuredHighlight tone="dark">{children}</MeasuredHighlight>
);

export const mintRichText: PortableTextComponents = {
  ...measuredRichTextDark,
  marks: {
    ...measuredRichTextDark.marks,
    darkGreenSerif: MintSerif,
    lightGreenSerif: MintSerif,
    lightGreenHighlight: MintHighlight,
    darkGreenHighlight: MintHighlight,
  },
};

const WS = /^[ \t\r\n]+$/;

function splitWords(node: ReactNode, path = 'w'): ReactNode[] {
  if (typeof node === 'string') {
    return node
      .split(/([ \t\r\n]+)/)
      .filter(Boolean)
      .map((part, i) =>
        WS.test(part) ? (
          part
        ) : (
          <span key={`${path}.${i}`} className="rze-m">
            <span className="rze-u">{part}</span>
          </span>
        ),
      );
  }
  if (Array.isArray(node)) return node.flatMap((child, i) => splitWords(child, `${path}.${i}`));
  if (isValidElement<{ children?: ReactNode }>(node) && node.props.children != null) {
    const inner = splitWords(node.props.children, path);
    const lead = typeof inner[0] === 'string' && WS.test(inner[0]) ? (inner.shift() as string) : '';
    const last = inner[inner.length - 1];
    const trail = typeof last === 'string' && WS.test(last) ? (inner.pop() as string) : '';
    return [lead, cloneElement(node, undefined, inner), trail].filter((part) => part !== '');
  }
  return [node];
}

function splitBlocks(base: PortableTextComponents): PortableTextComponents {
  const blocks = (typeof base.block === 'object' ? base.block : {}) as Record<
    string,
    ComponentType<{ children?: ReactNode }>
  >;
  const wrapped = Object.fromEntries(
    Object.entries(blocks).map(([style, Block]) => [
      style,
      (props: { children?: ReactNode }) => <Block {...props}>{splitWords(props.children)}</Block>,
    ]),
  );
  return { ...base, block: wrapped as PortableTextComponents['block'] };
}

type Mode = 'title' | 'text';

function useWordReveal<T extends HTMLElement = HTMLElement>(mode: Mode, delay: number) {
  const ref = useRef<T>(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const [armed, setArmed] = useState(false);
  const [shown, setShown] = useState(false);

  useIsoLayoutEffect(() => {
    if (!reduce) setArmed(true);
  }, [reduce]);

  useIsoLayoutEffect(() => {
    const root = ref.current;
    if (!root || !inView || !armed) return;
    const units = Array.from(root.querySelectorAll<HTMLElement>('.rze-u'));
    let line = -1;
    let top = -Infinity;
    units.forEach((unit, i) => {
      if (mode === 'text') {
        unit.style.transitionDelay = `${delay + i * 0.012}s`;
        return;
      }
      const box = unit.parentElement!.getBoundingClientRect();
      if (box.top - top > 0.5 * parseFloat(getComputedStyle(unit).fontSize)) {
        line += 1;
        top = box.top;
      }
      unit.style.transitionDelay = `${delay + line * 0.12}s`;
    });
    setShown(true);
  }, [inView, armed, mode, delay]);

  return {
    ref,
    className: cn('rze', mode === 'title' ? 'rze-title' : 'rze-text', armed && 'is-armed', shown && 'is-in'),
  };
}

export function RevealTitle({
  value,
  className,
  components = mintRichText,
}: {
  value?: PortableTextBlock[];
  className?: string;
  components?: PortableTextComponents;
}) {
  const split = useMemo(() => splitBlocks(components), [components]);
  const reveal = useWordReveal<HTMLDivElement>('title', 0);
  if (!value?.length) return null;
  return (
    <div ref={reveal.ref} className={cn('rz-mint', className, reveal.className)}>
      <PortableText value={value} components={split} />
    </div>
  );
}

export function RevealWords({
  text,
  as: Tag = 'p',
  delay = 0.25,
  className,
}: {
  text?: string;
  as?: ElementType;
  delay?: number;
  className?: string;
}) {
  const reveal = useWordReveal('text', delay);
  if (!text) return null;
  return (
    <Tag ref={reveal.ref} className={cn(className, reveal.className)}>
      {splitWords(text)}
    </Tag>
  );
}

export function RevealEyebrow({ text }: { text?: string }) {
  const reduce = useReducedMotion();
  if (!text) return null;
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.35 }}
      transition={{ opacity: { duration: 0.6, ease: 'easeInOut' }, y: { duration: 0.9, ease: EXPO } }}>
      <Eyebrow
        text={text}
        tone="dark"
      />
    </motion.div>
  );
}

export function RevealBlock({
  index = 0,
  className,
  children,
  as = 'div',
}: {
  index?: number;
  className?: string;
  children: ReactNode;
  as?: 'div' | 'li';
}) {
  const reduce = useReducedMotion();
  const Tag = as === 'li' ? motion.li : motion.div;
  const delay = Math.min(index * 0.08, 0.48);
  return (
    <Tag
      initial={reduce ? false : { opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.12, margin: '0px 0px -6% 0px' }}
      transition={{ opacity: { duration: 0.9, ease: EXPO, delay }, y: { duration: 1.1, ease: EXPO, delay } }}
      className={className}>
      {children}
    </Tag>
  );
}

export function CountUp({ value, className }: { value?: string; className?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { once: true, amount: 0.35 });
  const clean = stegaClean(value) ?? '';
  const spec = useMemo(() => {
    const match = clean.match(/^(\d+)(?:[.,](\d+))?$/);
    if (!match) return null;
    const decimals = match[2]?.length ?? 0;
    const sep = clean.includes(',') ? ',' : '.';
    return { target: Number(`${match[1]}.${match[2] ?? 0}`), decimals, sep };
  }, [clean]);
  const [display, setDisplay] = useState(clean);

  useIsoLayoutEffect(() => {
    if (spec && !reduce) setDisplay((0).toFixed(spec.decimals).replace('.', spec.sep));
  }, [spec, reduce]);

  useEffect(() => {
    if (!spec || reduce) {
      setDisplay(clean);
      return;
    }
    if (!inView) return;
    const controls = animate(0, spec.target, {
      duration: 1.5,
      delay: 0.25,
      ease: (p) => 1 - Math.pow(1 - p, 4),
      onUpdate: (n) => setDisplay(n.toFixed(spec.decimals).replace('.', spec.sep)),
      onComplete: () => setDisplay(clean),
    });
    return () => controls.stop();
  }, [inView, spec, reduce, clean]);

  return (
    <span
      ref={ref}
      className={cn('inline-grid', className)}>
      <span
        aria-hidden
        className="invisible col-start-1 row-start-1">
        {clean}
      </span>
      <span className="col-start-1 row-start-1 text-right">{display}</span>
    </span>
  );
}

export function RevealStyles() {
  return (
    <style
      jsx
      global>{`
      .rz-mint span:has(> .hl-wash) {
        color: #a8e8d4 !important;
      }
      @media (max-width: 992px) {
        .rz-mint.rz-tick-dark :is(.hl-tick-l, .hl-tick-r) > span {
          background-color: #228e71 !important;
        }
        .rz-mint.rz-tick-dark span:has(> .hl-wash) {
          --hl-sr: 0.75rem !important;
          --hl-sl: 0.6395rem !important;
        }
      }
      @media (min-width: 993px) {
        .rz-mint .hl-wash {
          background-color: rgba(34, 142, 113, 0.28) !important;
        }
        .rz-mint :is(.hl-tick-l, .hl-tick-r) > span {
          background-color: #4dc7a3 !important;
        }
        .rz-mint span:has(> .hl-wash) {
          --hl-sr: 0.5rem !important;
          --hl-sl: 0.5rem !important;
          --hl-r: 1px !important;
          --hl-tex: 0.9375rem !important;
        }
        .rz-mint .hl-tick-l {
          top: calc(var(--hl-top) + var(--rz-tl, 0.75rem)) !important;
          right: calc(100% + var(--hl-p-l) - 0.3125rem) !important;
        }
        .rz-mint .hl-tick-r {
          top: calc(var(--hl-top) - var(--hl-sr)) !important;
          left: calc(100% + var(--hl-p-r) - 0.3125rem) !important;
        }
        .rz-mint .hl-tick-r > span:last-child {
          height: calc(var(--hl-w) - 0.5rem) !important;
        }
      }
      .rze-m,
      .rze-u {
        display: inline-block;
        font-family: inherit;
      }
      .rze-text .rze-m {
        clip-path: inset(-0.25em -0.3em 0);
        padding-bottom: 0.12em;
        margin-bottom: -0.12em;
      }
      .rze-title .rze-m,
      .rze-title .rze-u {
        line-height: 0;
      }
      .rze-title .rze-m {
        clip-path: inset(-0.9em -0.3em -0.66em);
      }
      .rze-u {
        transition: transform 0.9s cubic-bezier(0.16, 1, 0.3, 1);
      }
      .rze.is-armed:not(.is-in) .rze-u {
        transition: none;
      }
      .rze-text.is-armed:not(.is-in) .rze-u {
        transform: translateY(115%);
      }
      .rze-title.is-armed:not(.is-in) .rze-u {
        transform: translateY(1.4em);
      }
      .rze :is(.hl-wash, .hl-texture, .hl-tick-l) {
        clip-path: inset(-20% -20% -20% 0);
        transition: clip-path 0.9s cubic-bezier(0.16, 1, 0.3, 1) 0.12s;
      }
      .rze .hl-tick-r {
        transition: opacity 0.25s ease 0.45s;
      }
      .rze.is-armed:not(.is-in) :is(.hl-wash, .hl-texture, .hl-tick-l) {
        clip-path: inset(-20% 100% -20% 0);
        transition: none;
      }
      .rze.is-armed:not(.is-in) .hl-tick-r {
        opacity: 0;
        transition: none;
      }
      @media (prefers-reduced-motion: reduce) {
        .rze .rze-u {
          transform: none !important;
          transition: none !important;
        }
        .rze :is(.hl-wash, .hl-texture, .hl-tick-l, .hl-tick-r) {
          clip-path: none !important;
          opacity: 1 !important;
          transition: none !important;
        }
      }
    `}</style>
  );
}
